import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Eye, ExternalLink, Tag, Maximize2, Zap, ShoppingBag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductCard({ product }) {
  const [views, setViews] = useState(product.views);
  const [modalOpen, setModalOpen] = useState(false);

  const handleClick = async () => {
    try {
      await axios.post(`${API}/products/${product.id}/view`);
      setViews(prev => prev + 1);
    } catch (error) {
      console.error('خطأ في تسجيل المشاهدة:', error);
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  // Check if product has a discount code
  const hasDiscountCode = product.discount_code && product.discount_code.trim() !== '';

  return (
    <>
      <Card 
        className="product-card group relative overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-200 rounded-2xl" 
        data-testid="product-card"
        dir="rtl"
      >
        <CardContent className="p-0">
          {/* Image Container */}
          <div className="relative overflow-hidden cursor-pointer bg-gradient-to-b from-gray-50 to-white" onClick={openModal}>
            <div className="aspect-square w-full overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-contain p-3 md:p-4 group-hover:scale-[1.02] transition-transform duration-300"
              />
            </div>
            
            {/* Discount Code Badge - Top Left (RTL: top right) - Only show if discount code exists */}
            {hasDiscountCode && (
              <div className="absolute top-3 right-3 z-10">
                <Badge 
                  className="bg-red-500 text-white px-3 py-1.5 rounded-full font-bold text-sm shadow-lg border-0 flex items-center gap-1"
                  data-testid="discount-code-badge"
                >
                  <Tag className="h-3 w-3" />
                  <span dir="ltr">{product.discount_code}</span>
                </Badge>
              </div>
            )}

            {/* Views Counter - Bottom Left (RTL: bottom right) */}
            <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2" data-testid="views-counter">
              <Eye className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-medium text-white">{views} مشاهدة</span>
            </div>

            {/* Quick View Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
              <div className="bg-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <Maximize2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">عرض سريع</span>
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="p-4 space-y-3">
            {/* Category Tag */}
            <div className="flex justify-start">
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {product.category === 'amazon' && 'أمازون'}
                {product.category === 'noon' && 'نون'}
                {product.category === 'namshi' && 'نمشي'}
                {product.category === 'aliexpress' && 'علي إكسبريس'}
              </span>
            </div>

            {/* Product Name */}
            <h3 
              className="text-base font-bold text-gray-900 line-clamp-2 leading-tight min-h-12 text-right" 
              data-testid="product-name"
            >
              {product.name}
            </h3>

            {/* Product Description */}
            <p 
              className="text-sm text-gray-600 line-clamp-2 leading-relaxed text-right" 
              data-testid="product-description"
            >
              {product.description}
            </p>

            {/* Price */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-xl font-bold text-primary" data-testid="product-price">
                {product.price}
              </span>
            </div>

            {/* CTA Button */}
            <a
              href={product.affiliate_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="block mt-4"
            >
              <Button 
                className="w-full bg-gradient-to-l from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-bold py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group"
                data-testid="affiliate-link-button"
              >
                <ShoppingBag className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                احصل على العرض
                <ExternalLink className="mr-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Product Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="product-modal" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-right text-gray-900">{product.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Product Image */}
            <div className="w-full rounded-xl overflow-hidden bg-gray-50">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-auto object-contain max-h-96"
              />
            </div>

            {/* Product Description */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-right text-gray-900">الوصف الكامل</h3>
              <p className="text-base text-gray-700 leading-relaxed text-right">
                {product.description}
              </p>
            </div>

            {/* Product Info */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-4">
              {/* Discount Code Section - Above Price, only if discount code exists */}
              {hasDiscountCode && (
                <div className="mb-4">
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium text-gray-700 text-right">كود الخصم</span>
                    <div className="border-2 border-dashed border-red-400 bg-gradient-to-l from-red-50 to-white rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <Tag className="h-6 w-6 text-red-500" />
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">{product.discount_code}</span>
                          <p className="text-sm text-gray-600 mt-1">انسخ الكود واستخدمه عند الدفع</p>
                        </div>
                      </div>
                      <button 
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-300 flex items-center gap-2"
                        onClick={() => {
                          navigator.clipboard.writeText(product.discount_code);
                        }}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                        نسخ
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Price Section */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">السعر:</span>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-primary">{product.price}</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <a
              href={product.affiliate_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="block"
            >
              <Button className="w-full bg-gradient-to-l from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300" data-testid="modal-affiliate-button">
                <ShoppingBag className="ml-3 h-6 w-6" />
                اشتري الآن واحصل على العرض
                <ExternalLink className="mr-3 h-5 w-5" />
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
